// typescript
import React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import CharCounter from "@/main/react/component/CharCounter";
import PasswordRevealToggle from "@/main/react/component/PasswordRevealToggle";
import { FloatingLabel } from "@/main/react/component/FloatingLabel";

type Props = React.ComponentProps<typeof Input> & {
    onEnter?: () => void;
    wrapperClassName?: string;
    /**
     * When provided (and `type="password"`), renders a show/hide toggle over the input. The labels
     * must already be localized; the component owns the reveal state and swaps the input type.
     */
    passwordReveal?: {
        showLabel: string;
        hideLabel: string;
        /**
         * The already-localized Caps Lock hint (`general.password.caps_lock_on`). Required, so every
         * password field with a reveal toggle also warns about Caps Lock (QoL QD-4).
         */
        capsLockLabel: string;
        playwrightId?: string;
    };
    /**
     * When provided, renders a Reddit/Facebook-style floating label: the (already-localized) text
     * rests inside the empty field like a placeholder and animates up to a small label on focus or
     * once the field has a value. Replaces the need for a separate label above the field. Requires
     * a stable `id` so the label's `htmlFor` resolves.
     */
    floatingLabel?: string;
    /** Fully suppress the character counter (e.g. credential-entry fields where a count is just noise). */
    hideCharCounter?: boolean;
};

export const InputWithCharCounter = React.forwardRef<HTMLInputElement, Props>(function InputWithCharCounter({
                                                          onEnter,
    wrapperClassName,
                                                          passwordReveal,
                                                          floatingLabel,
                                                          hideCharCounter = false,
                                                          ...props
                                                      }, ref) {
    const initialLength = (() => {
        if (props.value != null) return String(props.value).length;
        if (props.defaultValue != null) return String(props.defaultValue).length;
        return 0;
    })();

    const [length, setLength] = React.useState<number>(initialLength);
    const [passwordVisible, setPasswordVisible] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    const [capsLockOn, setCapsLockOn] = React.useState(false);

    React.useEffect(() => {
        if (props.value != null) {
            setLength(String(props.value).length);
        }
    }, [props.value]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setLength(e.target.value.length);
        props.onChange?.(e);
    };

    const revealEnabled = Boolean(passwordReveal) && props.type === "password";

    // QoL QD-4: read Caps Lock from every key event on a password field. Only ever set the same value
    // React already holds a no-op for, so typing does not re-render per keystroke once the state settles.
    const trackCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!revealEnabled || typeof e.getModifierState !== "function") {
            return;
        }
        const next = e.getModifierState("CapsLock");
        setCapsLockOn((current) => (current === next ? current : next));
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        trackCapsLock(e);
        const nativeEvent = e.nativeEvent as KeyboardEvent & {isComposing?: boolean};
        const shouldHandleEnter = e.key === "Enter"
            && !e.shiftKey
            && !e.altKey
            && !e.ctrlKey
            && !e.metaKey
            && !nativeEvent.isComposing;

        if (shouldHandleEnter && onEnter) {
            e.preventDefault();
            onEnter();
            return;
        }

        props.onKeyDown?.(e);
    };

    const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        trackCapsLock(e);
        props.onKeyUp?.(e);
    };

    const forwarded: React.ComponentProps<typeof Input> = {
        ...props,
        type: revealEnabled && passwordVisible ? "text" : props.type,
        className: cn(props.className, revealEnabled && "pr-10"),
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onKeyUp: handleKeyUp,
        onFocus: (event) => {
            setFocused(true);
            props.onFocus?.(event);
        },
        onBlur: (event) => {
            setFocused(false);
            setCapsLockOn(false);
            props.onBlur?.(event);
        },
    };

    // With a floating label the field reads as clean; keep the character counter
    // out of the way until the field is in use (focused or has content).
    const showCharCounter = !hideCharCounter && (floatingLabel == null || focused || length > 0);

    const revealToggle = revealEnabled && passwordReveal ? (
        <PasswordRevealToggle
            visible={passwordVisible}
            onToggle={() => setPasswordVisible((current) => !current)}
            showLabel={passwordReveal.showLabel}
            hideLabel={passwordReveal.hideLabel}
            playwrightId={passwordReveal.playwrightId}
        />
    ) : null;

    // The live region is mounted with the field (a region inserted together with its text is announced
    // far less reliably) and is empty, so zero height, until Caps Lock is on while the field is focused.
    const capsLockHint = revealEnabled && passwordReveal ? (
        <div role="status" aria-live="polite" data-playwright-id="password-caps-lock-region">
            {focused && capsLockOn ? (
                <p className="mt-1 mb-0 text-sm text-muted-foreground" data-playwright-id="password-caps-lock-hint">
                    {passwordReveal.capsLockLabel}
                </p>
            ) : null}
        </div>
    ) : null;

    if (floatingLabel) {
        return (
            <div className={`relative ${wrapperClassName ?? ""}`}>
                <div className="relative">
                    <Input
                        {...forwarded}
                        ref={ref}
                        placeholder=" "
                        // Reserve room on the right only for what is actually shown: the
                        // in-field counter and/or the password eye (forwarded already adds
                        // pr-10 for the eye alone), so the value never slides under them.
                        // `ACCOUNT-ACCESS-29` (A10): the floating-label pattern is start-aligned by
                        // definition, so the value declares it rather than inheriting a centred host's
                        // `text-center` (the account-access card). First, so a caller's own alignment wins.
                        className={cn(
                            "text-left",
                            forwarded.className,
                            "peer h-12 pt-5 pb-1",
                            revealEnabled && showCharCounter && "pr-20",
                            !revealEnabled && showCharCounter && "pr-14",
                        )}
                    />
                    <FloatingLabel htmlFor={props.id}>{floatingLabel}</FloatingLabel>
                    {revealToggle}
                    {showCharCounter ? (
                        <CharCounter
                            length={length}
                            min={props.minLength}
                            max={props.maxLength}
                            compact
                            className={cn(
                                "pointer-events-none absolute top-1/2 w-auto -translate-y-1/2 bg-transparent",
                                revealEnabled ? "right-11" : "right-3",
                            )}
                        />
                    ) : null}
                </div>
                {capsLockHint}
            </div>
        );
    }

    return (
        <div className={`relative ${wrapperClassName ?? ""}`}>
            <div className="relative">
                <label className="form__label" htmlFor={props.name}>
                    <Input {...forwarded} ref={ref} />
                </label>
                {revealToggle}
            </div>
            {capsLockHint}
            {/*
              `hideCharCounter` used to be honoured only on the floating-label branch, so a caller
              that suppressed the counter on a credential or confirmation field silently got one
              anyway whenever it did not also pass a floating label. The prop's own doc comment says
              "fully suppress"; this is that.
            */}
            {hideCharCounter ? null : (
                <CharCounter length={length} min={props.minLength} max={props.maxLength} />
            )}
        </div>
    );
});

export default InputWithCharCounter;
